import { formatTimestampBoardName, resolveBoardNameCollision } from "../domain/board_naming";
import { sha256 } from "../domain/content_hash";
import { inspectRasterImage, type RasterMimeType } from "../domain/image_format";
import { parseAssetPath } from "../domain/paths";
import { createEmptySceneJson, type EditorSnapshot } from "../editor/editor_snapshot";
import type { RecoveryRecord } from "./local_database";
import {
  type BoardAsset,
  type BoardDocument,
  boardDocumentSchema,
  parseBoardDocument,
  serializeBoardDocument,
} from "./board_schema";
import type { BoardMigrationLease } from "../workspace/board_lease";
import type { WorkspaceGateway } from "../workspace/workspace_gateway";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
const ASSET_ID_PREFIX = "sha256:";

export interface RecoveryPort {
  put(boardPath: readonly string[], documentJson: string, savedAt: Date): Promise<void>;
  get(boardPath: readonly string[]): Promise<RecoveryRecord | null>;
  clear(boardPath: readonly string[]): Promise<void>;
}

export interface BoardSessionOptions {
  readonly now?: () => Date;
  readonly createId?: () => string;
  readonly migrationLease?: BoardMigrationLease;
  readonly migrationLeaseKey?: string;
}

export interface BoardSaveResult {
  readonly document: BoardDocument;
  readonly previewSaved: boolean;
}

export interface BoardRecoveryCandidate {
  readonly document: BoardDocument;
  readonly record: RecoveryRecord;
}

export type PreviewProducer = () => Promise<Blob>;

interface PendingAsset {
  readonly bytes: Uint8Array<ArrayBuffer>;
  readonly metadata: BoardAsset;
}

/** Describes an invalid, newer, or incomplete board without altering it. */
export class BoardOpenError extends Error {
  public readonly kind: "invalid" | "newer" | "missing_asset";
  public readonly raw: string;

  /** Retains the non-destructive recovery category and raw canonical JSON. */
  public constructor(kind: BoardOpenError["kind"], message: string, raw: string) {
    super(message);
    this.name = "BoardOpenError";
    this.kind = kind;
    this.raw = raw;
  }
}

/** Stops a stale editor before it can replace a changed or removed disk document. */
export class BoardConflictError extends Error {
  public readonly expectedRevision: string | null;
  public readonly actualRevision: string | null;

  /** Retains both revisions for technical details without exposing board content. */
  public constructor(expectedRevision: string | null, actualRevision: string | null) {
    super("The disk copy changed while this board was open.");
    this.name = "BoardConflictError";
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}

/** Owns one board's durable assets, canonical document, and recovery ordering. */
export class BoardSession {
  private readonly editorKey = crypto.randomUUID();
  private readonly gateway: WorkspaceGateway;
  private readonly recovery: RecoveryPort;
  private readonly now: () => Date;
  private readonly createId: () => string;
  private readonly pendingAssets = new Map<string, PendingAsset>();
  private readonly assetBytes = new Map<string, Uint8Array<ArrayBuffer>>();
  private assets: Record<string, BoardAsset> = {};
  private path: readonly string[] | null;
  private document: BoardDocument | null;
  private draftId: string | null;
  private draftCreatedAt: string | null;
  private scene: string;
  private diskRevision: string | null;
  private recoveryCandidate: BoardRecoveryCandidate | null = null;

  /** Constructs sessions through the draft and open factories. */
  private constructor(
    gateway: WorkspaceGateway,
    recovery: RecoveryPort,
    path: readonly string[] | null,
    document: BoardDocument | null,
    scene: string,
    diskRevision: string | null,
    options: BoardSessionOptions,
  ) {
    this.gateway = gateway;
    this.recovery = recovery;
    this.path = path;
    this.document = document;
    this.draftId = document?.id ?? null;
    this.draftCreatedAt = document?.createdAt ?? null;
    this.scene = scene;
    this.diskRevision = diskRevision;
    this.now = options.now ?? (() => new Date());
    this.createId = options.createId ?? (() => crypto.randomUUID());
    if (document !== null) {
      this.assets = { ...document.assets };
    }
  }

  /** Creates a boardless editor session that becomes durable on first mutation. */
  public static createDraft(
    gateway: WorkspaceGateway,
    recovery: RecoveryPort,
    options: BoardSessionOptions = {},
  ): BoardSession {
    return new BoardSession(gateway, recovery, null, null, createEmptySceneJson(), null, options);
  }

  /** Opens and verifies a board before returning editor-compatible local data. */
  public static async open(
    gateway: WorkspaceGateway,
    recovery: RecoveryPort,
    path: readonly string[],
    options: BoardSessionOptions = {},
  ): Promise<BoardSession> {
    let canonicalBytes = await gateway.readFile([...path, "board.json"]);
    let raw = textDecoder.decode(canonicalBytes);
    const result = parseBoardDocument(raw);

    if (result.kind === "newer") {
      throw new BoardOpenError(
        "newer",
        `This board uses newer schema ${result.schemaVersion}.`,
        raw,
      );
    }
    if (result.kind === "invalid") {
      throw new BoardOpenError("invalid", result.reason, raw);
    }

    let document: BoardDocument;
    if (result.kind === "migration") {
      /** Re-reads under the writer lease before making any migration write. */
      const migrate = async (): Promise<{
        readonly bytes: Uint8Array<ArrayBuffer>;
        readonly document: BoardDocument;
        readonly raw: string;
      }> => {
        const currentBytes = await gateway.readFile([...path, "board.json"]);
        const currentRaw = textDecoder.decode(currentBytes);
        const currentResult = parseBoardDocument(currentRaw);
        if (currentResult.kind === "newer") {
          throw new BoardOpenError(
            "newer",
            `This board uses newer schema ${currentResult.schemaVersion}.`,
            currentRaw,
          );
        }
        if (currentResult.kind === "invalid") {
          throw new BoardOpenError("invalid", currentResult.reason, currentRaw);
        }
        if (currentResult.kind === "valid") {
          return { bytes: currentBytes, document: currentResult.document, raw: currentRaw };
        }

        await writeMigrationBackup(gateway, path, currentBytes, options.now?.() ?? new Date());
        const migratedRaw = serializeBoardDocument(currentResult.document);
        const migratedBytes = textEncoder.encode(migratedRaw);
        await gateway.writeFileSafely([...path, "board.json"], migratedBytes);
        return {
          bytes: migratedBytes,
          document: currentResult.document,
          raw: migratedRaw,
        };
      };
      const migrated =
        options.migrationLease === undefined
          ? await migrate()
          : await options.migrationLease.run(
              options.migrationLeaseKey ?? result.document.id,
              migrate,
            );
      canonicalBytes = migrated.bytes;
      document = migrated.document;
      raw = migrated.raw;
    } else {
      document = result.document;
    }

    const recoveryCandidate = await readNewerRecoveryCandidate(recovery, path, document);
    const allAssets = {
      ...document.assets,
      ...(recoveryCandidate?.document.assets ?? {}),
    };
    const files: Record<string, object> = {};
    const loadedAssetBytes = new Map<string, Uint8Array<ArrayBuffer>>();
    for (const [assetId, metadata] of Object.entries(allAssets)) {
      const relativeAssetPath = parseAssetPath(metadata.path);
      let bytes: Uint8Array<ArrayBuffer>;
      try {
        bytes = await gateway.readFile([...path, ...relativeAssetPath]);
      } catch {
        throw new BoardOpenError("missing_asset", `Missing board asset: ${metadata.path}.`, raw);
      }

      const actualHash = await sha256(bytes);
      if (
        bytes.byteLength !== metadata.byteLength ||
        assetId !== `${ASSET_ID_PREFIX}${actualHash}`
      ) {
        throw new BoardOpenError(
          "missing_asset",
          `Board asset failed verification: ${metadata.path}.`,
          raw,
        );
      }

      const editorFileId = assetId.slice(ASSET_ID_PREFIX.length);
      loadedAssetBytes.set(assetId, bytes);
      files[editorFileId] = {
        id: editorFileId,
        mimeType: metadata.mimeType,
        dataURL: await bytesToDataUrl(bytes, metadata.mimeType),
        created: Date.parse(document.createdAt),
      };
    }

    const scene = JSON.stringify({
      type: "excalidraw",
      version: 2,
      source: "shotboard",
      elements: document.editor.elements,
      appState: document.editor.appState,
      files,
    });
    const session = new BoardSession(
      gateway,
      recovery,
      path,
      document,
      scene,
      await sha256(canonicalBytes),
      options,
    );
    session.recoveryCandidate = recoveryCandidate;
    for (const [assetId, bytes] of loadedAssetBytes) {
      session.assetBytes.set(assetId, bytes);
    }
    return session;
  }

  /** Returns the current workspace-relative board path after its first mutation. */
  public get boardPath(): readonly string[] | null {
    return this.path;
  }

  /** Returns the public editor envelope created from validated local files. */
  public get sceneJson(): string {
    return this.scene;
  }

  /** Returns a stable React identity that changes only when the active session changes. */
  public get editorSessionKey(): string {
    return this.editorKey;
  }

  /** Returns the stable board document identity after canonical creation or open. */
  public get boardId(): string | null {
    return this.document?.id ?? this.draftId;
  }

  /** Exposes only a validated recovery document that is newer than canonical disk state. */
  public get pendingRecovery(): BoardRecoveryCandidate | null {
    return this.recoveryCandidate;
  }

  /** Returns the canonical timestamp used in recovery and conflict explanations. */
  public get canonicalUpdatedAt(): string | null {
    return this.document?.updatedAt ?? null;
  }

  /** Reads the latest browser-local recovery record without treating it as canonical. */
  public async readRecoveryRecord(): Promise<RecoveryRecord | null> {
    return this.path === null ? null : this.recovery.get(this.path);
  }

  /** Serializes the current editor state for an explicit recovery download without disk I/O. */
  public createRecoveryJson(snapshot: EditorSnapshot): string {
    return serializeBoardDocument(this.buildDocument(snapshot, this.now()));
  }

  /** Ignores editor callbacks that exactly reproduce the current canonical document. */
  public matchesCanonical(snapshot: EditorSnapshot): boolean {
    return this.document !== null && jsonEquals(documentSnapshot(this.document), snapshot);
  }

  /** Reports whether canonical board.json changed or disappeared since open or last save. */
  public async hasDiskConflict(): Promise<boolean> {
    if (this.path === null) {
      return false;
    }
    const actualRevision = await this.readDiskRevision(this.path);
    return actualRevision !== this.diskRevision;
  }

  /** Hashes and writes a pasted raster before returning its Excalidraw file identity. */
  public async registerAsset(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const format = inspectRasterImage(bytes, file.type);
    const hash = await sha256(bytes);
    const assetId = `${ASSET_ID_PREFIX}${hash}`;
    if (this.assets[assetId] !== undefined || this.pendingAssets.has(assetId)) {
      return hash;
    }

    const path = await this.ensureBoardPath();
    const metadata: BoardAsset = {
      path: `assets/${hash}.${format.extension}`,
      mimeType: format.mimeType,
      byteLength: bytes.byteLength,
      originalName: file.name.length > 0 ? file.name : `Pasted image.${format.extension}`,
    };
    this.pendingAssets.set(assetId, { bytes, metadata });
    this.assetBytes.set(assetId, bytes);
    await this.flushPendingAssets(path);
    return hash;
  }

  /** Stages the complete next document before the debounce can reach disk. */
  public async stageRecovery(snapshot: EditorSnapshot): Promise<void> {
    const path = await this.ensureBoardPath();
    const savedAt = this.now();
    const document = this.buildDocument(snapshot, savedAt);
    await this.recovery.put(path, serializeBoardDocument(document), savedAt);
  }

  /** Saves assets, then canonical JSON, then an optional replaceable preview. */
  public async save(
    snapshot: EditorSnapshot,
    previewProducer?: PreviewProducer,
  ): Promise<BoardSaveResult> {
    const path = await this.ensureBoardPath();
    await this.assertDiskRevisionCurrent(path);
    await this.flushPendingAssets(path);
    this.assertReferencedAssetsAreDurable(snapshot);
    const savedAt = this.now();
    const document = this.buildDocument(snapshot, savedAt);
    const documentJson = serializeBoardDocument(document);
    await this.recovery.put(path, documentJson, savedAt);
    await this.gateway.writeFileSafely([...path, "board.json"], textEncoder.encode(documentJson));
    this.diskRevision = await sha256(textEncoder.encode(documentJson));
    await this.recovery.clear(path);
    this.document = document;
    this.recoveryCandidate = null;

    let previewSaved = false;
    if (previewProducer !== undefined) {
      try {
        const preview = await previewProducer();
        await this.gateway.writeFileSafely(
          [...path, "preview.webp"],
          new Uint8Array(await preview.arrayBuffer()),
        );
        previewSaved = true;
      } catch {
        previewSaved = false;
      }
    }

    return { document, previewSaved };
  }

  /** Explicitly restores a newer validated browser-local recovery copy over unchanged disk state. */
  public async restoreRecovery(): Promise<BoardSession> {
    const path = this.path;
    const candidate = this.recoveryCandidate;
    if (path === null || candidate === null) {
      throw new Error("No newer recovery copy is available for this board.");
    }
    await this.assertDiskRevisionCurrent(path);
    const raw = serializeBoardDocument(candidate.document);
    const bytes = textEncoder.encode(raw);
    await this.gateway.writeFileSafely([...path, "board.json"], bytes);
    this.diskRevision = await sha256(bytes);
    await this.recovery.clear(path);
    return BoardSession.open(this.gateway, this.recovery, path, {
      now: this.now,
      createId: this.createId,
    });
  }

  /** Discards only the browser-local recovery candidate after explicit user confirmation. */
  public async discardRecovery(): Promise<void> {
    if (this.path !== null) {
      await this.recovery.clear(this.path);
    }
    this.recoveryCandidate = null;
  }

  /** Saves the validated recovery document as a new canonical board without touching the original. */
  public async saveRecoveryAsCopy(): Promise<BoardSession> {
    const candidate = this.recoveryCandidate;
    if (candidate === null) {
      throw new Error("No newer recovery copy is available for this board.");
    }
    return this.saveSnapshotAsCopy(
      documentSnapshot(candidate.document),
      undefined,
      candidate.document.assets,
    );
  }

  /** Saves the current in-memory editor state as a new board after a disk conflict or removal. */
  public async saveCurrentAsCopy(
    snapshot: EditorSnapshot,
    previewProducer?: PreviewProducer,
  ): Promise<BoardSession> {
    return this.saveSnapshotAsCopy(snapshot, previewProducer);
  }

  /** Rejects a canonical write when board.json no longer matches this editor session. */
  private async assertDiskRevisionCurrent(path: readonly string[]): Promise<void> {
    const actualRevision = await this.readDiskRevision(path);
    if (actualRevision !== this.diskRevision) {
      throw new BoardConflictError(this.diskRevision, actualRevision);
    }
  }

  /** Reads one current canonical fingerprint while treating a missing document as null. */
  private async readDiskRevision(path: readonly string[]): Promise<string | null> {
    const documentPath = [...path, "board.json"];
    if (!(await this.gateway.fileExists(documentPath))) {
      return null;
    }
    return sha256(await this.gateway.readFile(documentPath));
  }

  /** Creates the timestamp board directory exactly once. */
  private async ensureBoardPath(): Promise<readonly string[]> {
    if (this.path !== null) {
      return this.path;
    }

    const entries = await this.gateway.listDirectory([]);
    const takenNames = new Set(
      entries.filter((entry) => entry.kind === "directory").map((entry) => entry.name),
    );
    const name = resolveBoardNameCollision(formatTimestampBoardName(this.now()), takenNames);
    await this.gateway.createDirectory([name]);
    await this.gateway.createDirectory([name, "assets"]);
    this.path = [name];
    return this.path;
  }

  /** Writes every pending original byte sequence before canonical references. */
  private async flushPendingAssets(path: readonly string[]): Promise<void> {
    for (const [assetId, asset] of this.pendingAssets) {
      const assetPath = parseAssetPath(asset.metadata.path);
      if (!(await this.gateway.fileExists([...path, ...assetPath]))) {
        await this.gateway.writeFileSafely([...path, ...assetPath], asset.bytes);
      }
      this.assets[assetId] = asset.metadata;
      this.pendingAssets.delete(assetId);
    }
  }

  /** Copies original bytes into a collision-safe sibling and writes a fresh board identity. */
  private async saveSnapshotAsCopy(
    snapshot: EditorSnapshot,
    previewProducer?: PreviewProducer,
    assets: Readonly<Record<string, BoardAsset>> = this.assets,
  ): Promise<BoardSession> {
    const sourcePath = this.path;
    if (sourcePath === null) {
      throw new Error("An unsaved draft has no canonical board to copy.");
    }
    const parent = sourcePath.slice(0, -1);
    const sourceName = sourcePath.at(-1);
    if (sourceName === undefined) {
      throw new Error("The active board has no directory name.");
    }
    const entries = await this.gateway.listDirectory(parent);
    const copyName = resolveBoardNameCollision(
      `${sourceName} copy`,
      new Set(entries.map((entry) => entry.name)),
    );
    const copyPath = [...parent, copyName];

    const availableAssets = new Map<string, Uint8Array<ArrayBuffer>>();
    for (const [assetId, metadata] of Object.entries(assets)) {
      const bytes = this.assetBytes.get(assetId);
      if (bytes === undefined) {
        throw new Error(`The in-memory original asset is unavailable: ${metadata.path}.`);
      }
      availableAssets.set(assetId, bytes);
    }

    await this.gateway.createDirectory(copyPath);
    await this.gateway.createDirectory([...copyPath, "assets"]);

    const copy = new BoardSession(this.gateway, this.recovery, copyPath, null, this.scene, null, {
      now: this.now,
      createId: this.createId,
    });
    copy.assets = { ...assets };
    for (const [assetId, metadata] of Object.entries(copy.assets)) {
      const bytes = availableAssets.get(assetId);
      if (bytes === undefined) {
        throw new Error(`The validated in-memory original asset disappeared: ${metadata.path}.`);
      }
      copy.assetBytes.set(assetId, bytes);
      await this.gateway.writeFileSafely([...copyPath, ...parseAssetPath(metadata.path)], bytes);
    }
    await copy.save(snapshot, previewProducer);
    return BoardSession.open(this.gateway, this.recovery, copyPath, {
      now: this.now,
      createId: this.createId,
    });
  }

  /** Builds a schema-validated document while preserving stable board metadata. */
  private buildDocument(snapshot: EditorSnapshot, updatedAt: Date): BoardDocument {
    this.draftId ??= this.createId();
    this.draftCreatedAt ??= updatedAt.toISOString();
    return boardDocumentSchema.parse({
      format: "shotboard-board",
      schemaVersion: 1,
      id: this.draftId,
      createdAt: this.draftCreatedAt,
      updatedAt: updatedAt.toISOString(),
      editor: {
        engine: "excalidraw",
        engineVersion: "0.18.1",
        elements: snapshot.elements,
        appState: snapshot.appState,
        viewport: snapshot.viewport,
      },
      assets: this.assets,
    });
  }

  /** Prevents board.json from referencing an asset absent from its verified manifest. */
  private assertReferencedAssetsAreDurable(snapshot: EditorSnapshot): void {
    for (const element of snapshot.elements) {
      if (element.type === "image" && typeof element.fileId === "string") {
        if (this.assets[`${ASSET_ID_PREFIX}${element.fileId}`] === undefined) {
          throw new Error(
            "The editor referenced an image before its original asset became durable.",
          );
        }
      }
    }
  }
}

/** Returns one same-board recovery candidate only when it is newer than canonical state. */
async function readNewerRecoveryCandidate(
  recovery: RecoveryPort,
  path: readonly string[],
  canonical: BoardDocument,
): Promise<BoardRecoveryCandidate | null> {
  const record = await recovery.get(path);
  if (record === null) {
    return null;
  }
  const parsed = parseBoardDocument(record.documentJson);
  if (
    parsed.kind !== "valid" ||
    parsed.document.id !== canonical.id ||
    parsed.document.updatedAt <= canonical.updatedAt
  ) {
    return null;
  }
  return { document: parsed.document, record };
}

/** Converts one validated document editor envelope into a saveable snapshot. */
function documentSnapshot(document: BoardDocument): EditorSnapshot {
  return {
    elements: document.editor.elements,
    appState: document.editor.appState,
    viewport: document.editor.viewport,
  };
}

/** Compares JSON values structurally without depending on object property order. */
function jsonEquals(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => jsonEquals(value, right[index]));
  }
  if (typeof left !== "object" || left === null || typeof right !== "object" || right === null) {
    return false;
  }
  const leftRecord = Object.fromEntries(Object.entries(left));
  const rightRecord = Object.fromEntries(Object.entries(right));
  const leftKeys = Object.keys(leftRecord).sort();
  const rightKeys = Object.keys(rightRecord).sort();
  return (
    jsonEquals(leftKeys, rightKeys) &&
    leftKeys.every((key) => jsonEquals(leftRecord[key], rightRecord[key]))
  );
}

/** Writes one collision-safe timestamped byte-for-byte backup before schema migration. */
async function writeMigrationBackup(
  gateway: WorkspaceGateway,
  boardPath: readonly string[],
  canonicalBytes: Uint8Array<ArrayBuffer>,
  migratedAt: Date,
): Promise<void> {
  const timestamp = migratedAt
    .toISOString()
    .slice(0, 19)
    .replaceAll("-", "")
    .replace("T", "-")
    .replaceAll(":", "");
  const baseName = `board.pre-migration-${timestamp}`;
  const entries = await gateway.listDirectory(boardPath);
  const taken = new Set(entries.map((entry) => entry.name));
  let suffix = 1;
  let name = `${baseName}.json`;
  while (taken.has(name)) {
    suffix += 1;
    name = `${baseName}-${suffix}.json`;
  }
  await gateway.writeFileSafely([...boardPath, name], canonicalBytes);
}

/** Encodes verified local bytes for Excalidraw without fetching a remote URL. */
function bytesToDataUrl(bytes: Uint8Array<ArrayBuffer>, mimeType: RasterMimeType): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Shotboard could not decode a local image asset."));
      }
    });
    reader.addEventListener("error", () =>
      reject(new Error("Shotboard could not read an image asset.")),
    );
    reader.readAsDataURL(new Blob([bytes], { type: mimeType }));
  });
}
